// ============================================
// Date Range Picker Component
// ============================================

import { useState, useRef, useEffect } from 'react';
import { Calendar, ChevronDown, Check } from 'lucide-react';
import { useDateRange, DATE_PRESETS, DatePreset } from '../../stores/dateRangeStore';
import { formatDate } from '../../utils/formatters';

export function DateRangePicker() {
  const { dateRange, setDateRange, preset, setPreset } = useDateRange();
  const [isOpen, setIsOpen] = useState(false);
  const [customStart, setCustomStart] = useState(dateRange.start);
  const [customEnd, setCustomEnd] = useState(dateRange.end);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Update custom inputs when dateRange changes
  useEffect(() => {
    setCustomStart(dateRange.start);
    setCustomEnd(dateRange.end);
  }, [dateRange]);

  const handlePresetSelect = (presetValue: DatePreset) => {
    if (presetValue !== 'custom') {
      setPreset(presetValue);
      setIsOpen(false);
    }
  };

  const handleCustomApply = () => {
    if (customStart && customEnd && customStart <= customEnd) {
      setDateRange({ start: customStart, end: customEnd });
      setIsOpen(false);
    }
  };

  const displayText = preset === 'custom'
    ? `${formatDate(dateRange.start)} - ${formatDate(dateRange.end)}`
    : DATE_PRESETS.find(p => p.value === preset)?.label || '30 ngày qua';

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Trigger Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
      >
        <Calendar className="w-4 h-4 text-gray-500" />
        <span className="text-sm font-medium text-gray-700">{displayText}</span>
        <ChevronDown className={`w-4 h-4 text-gray-500 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {/* Dropdown */}
      {isOpen && (
        <div className="absolute right-0 mt-2 w-80 bg-white rounded-lg shadow-lg border border-gray-200 z-50">
          {/* Presets */}
          <div className="p-2 border-b border-gray-100">
            <p className="text-xs font-medium text-gray-500 px-2 mb-2">Chọn nhanh</p>
            <div className="grid grid-cols-2 gap-1">
              {DATE_PRESETS.filter(p => p.value !== 'custom').map((presetOption) => (
                <button
                  key={presetOption.value}
                  onClick={() => handlePresetSelect(presetOption.value)}
                  className={`flex items-center justify-between px-3 py-2 text-sm rounded-md transition-colors ${
                    preset === presetOption.value
                      ? 'bg-blue-50 text-blue-700'
                      : 'hover:bg-gray-50 text-gray-700'
                  }`}
                >
                  {presetOption.label}
                  {preset === presetOption.value && <Check className="w-4 h-4" />}
                </button>
              ))}
            </div>
          </div>

          {/* Custom Range */}
          <div className="p-4">
            <p className="text-xs font-medium text-gray-500 mb-3">Tùy chọn khoảng thời gian</p>
            <div className="flex items-center gap-2 mb-3">
              <div className="flex-1">
                <label className="text-xs text-gray-500 mb-1 block">Từ ngày</label>
                <input
                  type="date"
                  value={customStart}
                  onChange={(e) => setCustomStart(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-200 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div className="flex-1">
                <label className="text-xs text-gray-500 mb-1 block">Đến ngày</label>
                <input
                  type="date"
                  value={customEnd}
                  onChange={(e) => setCustomEnd(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-200 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>
            <button
              onClick={handleCustomApply}
              className="w-full px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 transition-colors"
            >
              Áp dụng
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
